import { TaskChair } from '../parts/seating'
import type { AssetContext, AssetDefinition } from '../types'

/** Standard operative seat height: the shared sub-assembly's own default. */
const SEAT_HEIGHT = 0.46
/** The executive shell rides one gas-lift notch higher than the operative one. */
const EXEC_SEAT_HEIGHT = 0.5

/**
 * Swivel task chair — a thin wrapper over the shared `TaskChair` assembly.
 *
 * Origin at the centre of the 0.66 x 0.66 footprint, y = 0 on the floor, the
 * seat opening (and therefore the sitter) facing +Z. The whole chair is
 * "equipment", so it stays charcoal; the accent only reaches the backrest on
 * the `color` variant, through `backColor`.
 */
function ChairTask({ accent, variant = 'estandar' }: AssetContext) {
  const executive = variant === 'ejecutiva'
  const upholstered = variant === 'color'

  return (
    <TaskChair
      seatHeight={executive ? EXEC_SEAT_HEIGHT : SEAT_HEIGHT}
      armrests={variant !== 'simple'}
      headrest={executive}
      // `accent` may be undefined (no per-instance override): the accent tone
      // then supplies the theme colour instead of falling back to charcoal.
      backTone={upholstered ? 'accent' : 'dark'}
      backColor={upholstered ? accent : undefined}
    />
  )
}

export const definition: AssetDefinition = {
  id: 'chair-task',
  name: 'Silla operativa',
  category: 'seating',
  footprint: [0.66, 0.66],
  // Backrest top of the operative shell. The `ejecutiva` variant reaches ~1.24
  // with its headrest and taller lift.
  height: 1.06,
  seats: 1,
  occupantSide: 'front',
  seatInset: 0.33,
  accentable: true,
  variants: [
    { id: 'estandar', name: 'Estándar' },
    { id: 'ejecutiva', name: 'Ejecutiva' },
    { id: 'simple', name: 'Sin brazos' },
    { id: 'color', name: 'Respaldo en color' },
  ],
  keywords: [
    'silla',
    'operativa',
    'giratoria',
    'ergonómica',
    'ruedas',
    'escritorio',
    'task chair',
    'office chair',
    'swivel chair',
    'desk chair',
  ],
  Component: ChairTask,
}
